/**
 * @file Mobile-only read-only Recall row viewer.
 */

import { appData } from "../../app/state/state.js";
import {
    escapeHTML,
    formatAndSanitizeUserContent,
} from "../../shared/utils/contentUtils.js";
import { formatDate } from "../../shared/utils/dateUtils.js";
import { formatRecallsSystemNoteContent } from "./recallsSystemNoteContent.js";
import { recallsRouteState } from "./recallsState.js";
import { getRecallReasonDisplay } from "./recallsReasonUtils.js";
import {
    getRecallProviderDisplay,
} from "./recallsProviderUtils.js";

const MODAL_ID = "recalls-mobile-read-modal";
const MOBILE_RECALL_VIEW_QUERY = "(max-width: 767px)";

let activeKeydownAbort = null;

function isMobileRecallView() {
    return window.matchMedia?.(MOBILE_RECALL_VIEW_QUERY).matches === true;
}

function getPatientName(item = {}) {
    return [item.first_name, item.last_name].filter(Boolean).join(" ");
}

function getStatusLabel(statusId) {
    const status = (appData.config?.recallStatus || [])
        .find((option) => option?.id === statusId);
    return status?.name || statusId || "Unassigned";
}

function getRecallReasonLabel(item = {}) {
    return getRecallReasonDisplay(
        item.recall_reason,
        appData.config?.recalls?.recallReasons || [],
    );
}

function getLatestNoteText(item = {}) {
    if (item.latestNote?.isSystemNote === true) {return "";}
    return formatRecallsSystemNoteContent(item.latestNote?.text || "").trim();
}

function getCachedRecall(recallId) {
    return [
        ...(appData.recalls || []),
        ...(appData.closedRecalls || []),
        ...(recallsRouteState.advancedSearch?.results || []),
    ].find((item) =>
        item?.id === recallId && item.__recallDraft !== true) || null;
}

function renderField(label, value) {
    const displayValue = String(value || "").trim() || "-";
    return `
        <div class="text-sm leading-5 text-gray-800">
            <dt class="inline font-semibold text-gray-900">${escapeHTML(label)}:</dt>
            <dd class="inline break-words text-gray-700"> ${escapeHTML(displayValue)}</dd>
        </div>
    `;
}

function renderNoteField(noteText) {
    const displayText = String(noteText || "").trim();
    return `
        <div class="text-sm leading-5 text-gray-800">
            <dt class="inline font-semibold text-gray-900">Latest Note:</dt>
            <dd class="inline text-gray-700">
                ${displayText ? ` ${formatAndSanitizeUserContent(displayText)}` : " -"}
            </dd>
        </div>
    `;
}

function renderModalContent(item = {}) {
    const patientName = getPatientName(item) || "Unnamed Recall";
    return `
        <div
            class="flex items-start justify-between gap-3 border-b border-gray-200 px-4 py-3"
        >
            <div class="min-w-0">
                <h2 class="truncate text-base font-semibold text-gray-900">
                    ${escapeHTML(patientName)}
                </h2>
                <p class="mt-0.5 truncate text-sm text-gray-500">
                    ${escapeHTML(getStatusLabel(item.status))}
                </p>
            </div>
            <button
                type="button"
                class="recalls-mobile-read-close standard-action-icon-btn standard-action-icon-btn--danger shrink-0"
                aria-label="Close recall details"
            >
                <span class="material-symbols-outlined text-[20px]">close</span>
            </button>
        </div>
        <div class="min-h-0 flex-1 overflow-y-auto px-4 py-3">
            <dl
                class="space-y-2"
            >
                ${renderField("Email", item.email || "No email")}
                ${renderField("Phone", item.phone || "No phone")}
                ${renderField("Reason", getRecallReasonLabel(item))}
                ${renderField(
                    "Provider",
                    getRecallProviderDisplay(item.provider),
                )}
                ${renderField(
                    "Recall Date",
                    formatDate(item.recall_date) || item.recall_date,
                )}
                ${getLatestNoteText(item) ? renderNoteField(getLatestNoteText(item)) : ""}
            </dl>
        </div>
    `;
}

function ensureModal() {
    let modal = document.getElementById(MODAL_ID);
    if (modal) {return modal;}

    modal = document.createElement("div");
    modal.id = MODAL_ID;
    modal.className =
        "fixed inset-0 z-50 hidden items-center justify-center bg-black/40 p-3";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    modal.innerHTML = `
        <div
            class="flex max-h-[calc(100vh-1.5rem)] w-full max-w-sm flex-col rounded-lg bg-white shadow-xl"
        >
            <div data-recalls-mobile-read-content></div>
        </div>
    `;
    document.body.append(modal);
    return modal;
}

/**
 * Closes the mobile Recall read-only modal.
 * @returns {void}
 */
export function closeRecallMobileReadModal() {
    activeKeydownAbort?.abort();
    activeKeydownAbort = null;
    const modal = document.getElementById(MODAL_ID);
    modal?.classList.add("hidden");
    modal?.classList.remove("flex");
}

function bindModal(modal) {
    modal.querySelectorAll(".recalls-mobile-read-close").forEach((button) => {
        button.onclick = closeRecallMobileReadModal;
    });
    activeKeydownAbort?.abort();
    activeKeydownAbort = new AbortController();
    document.addEventListener("keydown", (event) => {
        if (event.key === "Escape") {
            closeRecallMobileReadModal();
        }
    }, { signal: activeKeydownAbort.signal });
}

function openRecallMobileReadModal(recallId) {
    if (!isMobileRecallView()) {return;}
    const item = getCachedRecall(recallId);
    if (!item?.id) {return;}
    const modal = ensureModal();
    const content = modal.querySelector("[data-recalls-mobile-read-content]");
    if (!content) {return;}
    content.innerHTML = renderModalContent(item);
    bindModal(modal);
    modal.classList.remove("hidden");
    modal.classList.add("flex");
}

function isInteractiveTarget(target) {
    if (!(target instanceof Element)) {return false;}
    return Boolean(target.closest([
        "a",
        "button",
        "input",
        "select",
        "textarea",
        "[role=\"button\"]",
        "[data-recalls-inline-root]",
        "[data-recalls-control-wrapper]",
    ].join(",")));
}

/**
 * Binds mobile-only read modal row clicks for permanent Recall rows.
 * @param {HTMLElement} container Recalls route container.
 * @returns {void}
 */
export function bindRecallsMobileReadActions(container) {
    container.querySelectorAll("[data-recalls-mobile-view]")
        .forEach((row) => {
            row.addEventListener("click", (event) => {
                if (!isMobileRecallView() || isInteractiveTarget(event.target)) {
                    return;
                }
                openRecallMobileReadModal(row.dataset.recallsMobileView);
            });
        });
}
